<script setup>
import { ref, computed, watch } from 'vue';
import { VueFlow } from '@vue-flow/core';
import { Controls } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';
import { transformWorkflowData } from '../utils/transformWorkflowData';

const props = defineProps({
  workflowData: {
    type: Object,
    default: () => null,
  },
  theme: {
    type: String,
    default: 'light',
  },
});

const nodes = ref([]);
const edges = ref([]);

const colorsListLight = [
  '#22C55E',
  '#3B82F6',
  '#8B5CF6',
  '#F59E0B',
  '#EF4444',
  '#6B7280',
];
const colorsListDark = [
  '#16A34A',
  '#2563EB',
  '#7C3AED',
  '#D97706',
  '#DC2626',
  '#4B5563',
];

const colorsList = computed(() => {
  return props.theme === 'dark' ? colorsListDark : colorsListLight;
});

const nodeColor = node => {
  return node.style?.backgroundColor || '#6B7280';
};

// Transform workflow data whenever it changes
watch(
  [() => props.workflowData, colorsList],
  () => {
    if (props.workflowData) {
      const transformedData = transformWorkflowData(
        props.workflowData,
        colorsList.value
      );
      nodes.value = transformedData.nodes;
      edges.value = transformedData.edges;
    }
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <div class="workflow-chart-container">
    <VueFlow
      v-model:nodes="nodes"
      v-model:edges="edges"
      fit-view-on-init
      :fit-view-options="{ padding: 0.1, includeHiddenNodes: false }"
      nodes-draggable
      nodes-connectable
      elements-selectable
      pan-on-drag
      zoom-on-scroll
      class="vue-flow-workflow"
    >
      <!-- <Background :gap="16" /> -->
      <Controls />
      <MiniMap
        :node-color="nodeColor"
        :node-stroke-width="0"
        zoomable
        pannable
        :mask-color="
          theme === 'dark'
            ? 'rgba(27, 31, 59, 0.6)'
            : 'rgba(204, 204, 204, 0.6)'
        "
      />
    </VueFlow>
  </div>
</template>

<style scoped>
.workflow-chart-container {
  min-height: 400px;
  width: 100%;
  height: 100%;
}

.vue-flow-workflow {
  background-color: var(--vf-surface);
  width: 100%;
  height: 100%;
}

/* Custom node styles */
:deep(.vue-flow__node) {
  font-size: 12px;
  font-weight: bold;
}

:deep(.vue-flow__edge) {
  stroke-width: 2px;
}

:deep(.vue-flow__minimap) {
  border-radius: 8px;
  overflow: hidden;
}

:deep(.vue-flow__controls) {
  border-radius: 8px;
  overflow: hidden;
}

/* Ensure the flow container takes full width for horizontal layout */
:deep(.vue-flow) {
  width: 100% !important;
  height: 100% !important;
}

:deep(.vue-flow__viewport) {
  width: 100% !important;
  height: 100% !important;
}
</style>
